/*───────────────────────────────────────────────────────────────────────────*\
  File      : src/utils/galleryApi.ts;
  Purpose   : Client-side helper that wraps the /api/gallery/* endpoints in
              type-safe Promise functions for UI developers. Each function
              returns the parsed JSON response without throwing for HTTP
              errors so callers can rely on the `{ success, error }` pattern.;
\*───────────────────────────────────────────────────────────────────────────*/

import type {
  GalleryListRequest,
  GalleryListResponse,
  LikeImageRequest,
  LikeImageResponse,
} from "@/src/types/gallery";

/*=============================================================================
  ▸ Internal helpers
=============================================================================*/

/**
 * POST helper that always includes credentials and JSON headers.;
 * @param path API route path (absolute or relative).;
 * @param payload Request body object to serialize as JSON.;
 */
async function post<T>(path: string, payload: object): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return (await res.json()) as T;
}

/**
 * DELETE helper that includes credentials and JSON headers.;
 * @param path API route path (absolute or relative).;
 * @param payload Request body object to serialize as JSON.;
 */
async function del<T>(path: string, payload: object): Promise<T> {
  const res = await fetch(path, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return (await res.json()) as T;
}

/*=============================================================================
  ▸ Gallery endpoints
=============================================================================*/

/**
 * List public gallery images (fabricated-only) with server-side filters,
 * pagination, and sorting.;
 * @param payload Gallery list request payload.;
 * @returns Parsed JSON response envelope with `{ success, page, pageSize, total, items }`.;
 */
export function listGallery(payload: GalleryListRequest): Promise<GalleryListResponse> {
  return post<GalleryListResponse>("/api/gallery/list", payload);
}

/**
 * Like a gallery image (idempotent).;
 * @param imageId Target gallery image UUID.;
 * @param userId Liking user UUID (must be authenticated on the server).;
 * @returns Parsed JSON response with `{ success, likeCount }` or `{ success: false, error }`.;
 */
export function likeImage(imageId: string, userId: string): Promise<LikeImageResponse> {
  const payload: LikeImageRequest & { userId: string } = { imageId, userId };
  return post<LikeImageResponse>("/api/gallery/like", payload);
}

/**
 * Unlike a gallery image (idempotent).;
 * @param imageId Target gallery image UUID.;
 * @param userId Unliking user UUID (must be authenticated on the server).;
 * @returns Parsed JSON response with `{ success, likeCount }` or `{ success: false, error }`.;
 */
export function unlikeImage(imageId: string, userId: string): Promise<LikeImageResponse> {
  const payload: LikeImageRequest & { userId: string } = { imageId, userId };
  return del<LikeImageResponse>("/api/gallery/like", payload);
}

/*──────────────────────────── End of file ────────────────────────────────*/

